import {AsyncPipe} from "@angular/common";
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {ProgressSpinner} from 'primeng/progressspinner';
import {filter, shareReplay, take} from 'rxjs';
import typia from "typia";
import {AnalyticsService} from '../../../services/analytics.service';
import {UserService, WaitResult} from '../../../services/user.service';
import {MessageService} from "primeng/api";
import {Location} from '@angular/common';
import {claimFirstVisit} from '../claim-first-visit';
import {leaveOutOfBackHistory} from '../../../services/back-navigation.service';
import {readStripeCheckoutReturn} from '../stripe-checkout-return';

@Component({
  selector: 'homey-deferred-nt-success-page',
  standalone: true,
  imports: [ProgressSpinner, AsyncPipe],
  templateUrl: './deferred-nt-success-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeferredNoTrialSuccessPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly location = inject(Location);
  private readonly isInterval = typia.createIs<'annual' | 'monthly'>();
  protected readonly settled$ = this.userService.waitUntilDeferredPaymentSettled().pipe(
    shareReplay(1),
  );

  constructor() {
    const intervalFromQuery = this.route.snapshot.queryParamMap.get('interval');
    const interval = this.isInterval(intervalFromQuery) ? intervalFromQuery : 'unknown';
    // The browser's Back, a reload or Forward into this entry comes through here again (the app's
    // own Back can't, see leaveOutOfBackHistory): don't count the payment twice or re-open the trial
    // dialog then.
    const firstVisit = claimFirstVisit(this.location);
    if (firstVisit) {
      void this.analyticsService.sendPaymentSuccess({
        flow: 'initial',
        checkoutLocation: 'deferred',
        interval,
        checkout: readStripeCheckoutReturn(this.route.snapshot.queryParamMap),
      });
    }

    this.settled$
      .pipe(
        filter((result): result is 'done' | 'unauthorized' | 'timed-out' => result !== 'waiting'),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe(result => {
        if (result === 'done') {
          void this.router.navigate(
            ['/profile', 'update', 'income-assets-info', 'complete'],
            // Out of the app's Back, whose fresh entry would claim a first visit again. The browser keeps
            // the entry: before it is the finished Stripe Checkout session (Piet, #726).
            {queryParams: firstVisit ? {payment: 'trial-started'} : {}, state: leaveOutOfBackHistory}
          );
        } else if (result === 'unauthorized') {
          this.messageService.add({severity: 'warn', summary: 'Session expired', detail: 'Please log in again.'});
          void this.router.navigateByUrl('/login', {state: leaveOutOfBackHistory});
        } else {
          this.messageService.add({severity: 'warn', summary: 'Timed out', detail: 'Redirected back to home.'});
          void this.router.navigateByUrl('/', {state: leaveOutOfBackHistory});
        }
      });
  }
}
